import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { TrackingHandler } from 'client/tracking/handler';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { getTypeArticlesLandingPath } from 'site-modules/editorial/utils/content-path';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { getTypeArticlesLandingBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { transparentLayer } from 'client/images/inline-svgs/ads/home-hero-background';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { BrandHero } from 'site-modules/shared/components/brand-hero/brand-hero';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { BrandSubnav } from 'site-modules/editorial/components/type/type-articles-landing/brand-subnav/brand-subnav';
import { ResearchEntry } from 'site-modules/editorial/components/type/type-articles-landing/research-entry/research-entry';

import './type-articles-landing.scss';

function TypeArticlesLandingUI({ content = null, isMobile = false, type = '' }) {
  const [isVideoModalOpen, toggleVideoModal] = useToggle(false);
  const [videoModalData, setVideoModalData] = useState(null);

  useEffect(() => {
    // eslint-disable-next-line
    TrackingHandler.useMap(EditorialMap);
  }, []);

  const openVideoModal = useCallback(
    videoData => {
      setVideoModalData(videoData);
      toggleVideoModal();
    },
    [toggleVideoModal]
  );

  if (!content) {
    return null;
  }

  const { title } = content;
  const mainEntries = content.child('main').children();

  const subnavItems = content
    .child('subnav')
    .links()
    .map(({ href: hash, title: name }) => ({ name, hash }));

  const treehousePromoProps = {
    className: 'bg-white rounded-12 px-1_5 py-1_5',
    heading: 'Am I Ready for an EV?',
    creativeId: 'treehouse-promotion',
  };

  return (
    <main className="type-articles-landing">
      <BrandHero breadcrumbsData={getTypeArticlesLandingBreadcrumbs(title, type)} hasLogo isMobile={isMobile}>
        <Row>
          <Col xs={12} md={7} className="px-0">
            {transparentLayer()}
            <div className="header-container px-0_5 px-0 px-md-0 py-lg-2_5 px-lg-3">
              <h1 className="title fw-bold text-white mb-0">{title}</h1>
            </div>
          </Col>
        </Row>
      </BrandHero>
      <BrandSubnav className="mb-3" items={subnavItems} isMobile={isMobile} />
      {mainEntries.map((entry, index) => (
        <ResearchEntry
          key={entry.id}
          entry={entry}
          setVideoModalData={openVideoModal}
          isMobile={isMobile}
          isLazyLoadImages={!!index}
        />
      ))}
      {type === 'electric-car' && ( // Conditional rendering for electric-car type
        <Container>
          <TreehousePromotionCheck>
            <Row className="my-3_5 justify-content-center">
              <Col xs={12} md={8}>
                <div className="bg-cool-gray-90 px-1 py-1 rounded-12">
                  <ReadyForEvTreehouse {...treehousePromoProps} />
                </div>
              </Col>
            </Row>
          </TreehousePromotionCheck>
          <PageLinksWidget divideSections={false} title="Related information" hasTopDivider={false} />
        </Container>
      )}
      <YoutubeVideoModalAsync
        onToggleModal={toggleVideoModal}
        isOpen={isVideoModalOpen}
        videoId={get(videoModalData, 'videoId')}
        videoAspectRatio={get(videoModalData, 'videoAspectRatio')}
        creativeId={get(videoModalData, 'creativeId')}
        videoTitle={get(videoModalData, 'videoTitle')}
      />
    </main>
  );
}

TypeArticlesLandingUI.propTypes = {
  content: CmsEntities.Content,
  isMobile: PropTypes.bool,
  type: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
});

export const TypeArticlesLanding = connect(mapStateToProps)(
  connectToModel(TypeArticlesLandingUI, {
    content: bindToContent(({ type }) => getTypeArticlesLandingPath(type)),
  })
);
